import type React from 'react';

import * as styles from './styles';

import type { Sort, SortOrder } from '@cobliteam/common-types/src';

import { type Checklist, type ChecklistsPage, isWebDataSuccess, type WebData } from '@cobliteam/api-lib';

import { type ITableColumn, Table, TableLoading } from '@cobliteam/base-components';

import { TabPanelOptions } from '../../constants';

export interface ChecklistsTableProps<T extends Checklist> {
  checklistsPageWebData: WebData<ChecklistsPage>;
  columns: Array<ITableColumn<Checklist>>;
  selectedPanel: TabPanelOptions;
  emptyState: React.ReactNode;
  sortBy?: Sort;
  backendPagination?: boolean;
  onEdit: (row: Checklist) => void;
  onRequestSort?: (column: string, order: SortOrder) => void;
  onPaginationChange?: (newPage: number, newRowsPerPage: number) => void;
}

export const ChecklistsTable = <T extends Checklist>({
  checklistsPageWebData,
  columns,
  emptyState,
  sortBy,
  selectedPanel,
  onEdit,
  onPaginationChange,
  backendPagination,
  onRequestSort,
}: ChecklistsTableProps<T>) => {
  const handleRequestSort = (column: ITableColumn<Checklist>, order: SortOrder) => {
    if (onRequestSort) {
      onRequestSort(column.id.toString(), order);
    }
  };

  if (isWebDataSuccess(checklistsPageWebData)) {
    const checklists = checklistsPageWebData.data.page.content;
    const checklistsPage = checklistsPageWebData.data.page;

    if (checklistsPageWebData.data.page.content.length === 0) {
      return <>{emptyState}</>;
    }

    return (
      <Table
        tableTitle='Checklists'
        orderBy={(sortBy && columns.find((column) => column.id === sortBy.column)) || columns[0]}
        order={sortBy?.order}
        onEdit={onEdit}
        customActionButtonName={selectedPanel === TabPanelOptions.RECEIVED_LISTS ? 'Ver checklist' : 'Editar'}
        columns={columns}
        rows={checklists}
        rowsPerPage={checklistsPage.size}
        className={styles.table}
        selected={checklists.slice(0, checklists.length / 2)}
        totalRowsLength={checklistsPage.totalElements}
        currentPage={checklistsPage.number}
        onPaginationChange={onPaginationChange}
        onRequestSort={handleRequestSort}
        pagination={selectedPanel === TabPanelOptions.RECEIVED_LISTS}
        backendPagination={backendPagination}
      />
    );
  }

  const totalRenderedColumns = columns.filter((column) => !column.hide).length;
  return <TableLoading numOfRows={25} numOfColumns={totalRenderedColumns + 1} />;
};

export default ChecklistsTable;
